import { DepositForAccount, DepositMigrationResult } from "@mezo/api"
import {
  MIGRATABLE_TOKENS,
  MINUTE,
  SECOND,
  SupportedTokenName,
} from "@mezo/sdk"
import { DateTime } from "luxon"

import { DATABASE_DATE_FORMAT, LOCK_TIMES } from "#/shared/constants"

import { DepositMigrationStatus } from "../types"

export const getUnlockTime = (unlockAt: number | undefined): string | null => {
  const currentDate = new Date()
  currentDate.setUTCHours(currentDate.getUTCHours())

  const currentTime = DateTime.fromISO(currentDate.toISOString())

  const endTime = unlockAt
    ? DateTime.fromSeconds(Number(unlockAt / 1000))
    : currentTime

  const unlockTime =
    currentTime > endTime ? null : endTime.toFormat("MMMM d, yyyy")

  return unlockTime
}

export const getLockData = (
  lockPeriod: number,
): { lockStart: string; lockEnd: string } => {
  const currentDate = new Date()
  currentDate.setUTCHours(currentDate.getUTCHours())

  const currentTimestamp = currentDate.getTime()
  const lockEndTimestamp = currentTimestamp + lockPeriod * SECOND

  const lockStart = DateTime.fromMillis(currentTimestamp)
    .setZone("UTC")
    .toFormat(DATABASE_DATE_FORMAT)

  const lockEnd = DateTime.fromMillis(lockEndTimestamp)
    .setZone("UTC")
    .toFormat(DATABASE_DATE_FORMAT)

  return { lockStart, lockEnd }
}

export const getTotalDepositsAmount = (deposits: DepositForAccount[]) => {
  const depositedAmounts = deposits.map((deposit) =>
    BigInt(deposit.amount ?? "0"),
  )

  const depositsSum = depositedAmounts.length
    ? depositedAmounts.reduce((a, b) => a + b)
    : 0n

  return depositsSum
}

export const calculateLockDuration = (
  lockStart: Date,
  lockEnd: Date,
): number => {
  const lockStartDate = DateTime.fromISO(new Date(lockStart).toISOString())
  const lockEndDate = DateTime.fromISO(new Date(lockEnd).toISOString())

  return Math.round(lockEndDate.diff(lockStartDate, "weeks").toObject().weeks!)
}

export const calculateWeeksLockTimeLeft = (lockEnd: Date) => {
  const timezoneOffset = new Date().getTimezoneOffset()

  const currentTime = DateTime.fromJSDate(
    new Date(Date.now() + timezoneOffset * MINUTE),
  )

  const endTime = DateTime.fromJSDate(new Date(lockEnd))

  return endTime.diff(currentTime, ["weeks"]).toObject().weeks!
}

export const getRecentDepositLock = (deposit: DepositForAccount) =>
  deposit.locks[deposit.locks.length - 1]

export const sortDepositsByLockEnd = (
  deposits: DepositForAccount[],
  sortOrder: "asc" | "desc",
) =>
  deposits.sort((a, b) => {
    const aLockTime = new Date(getRecentDepositLock(a).lockEnd).getTime()
    const bLockTime = new Date(getRecentDepositLock(b).lockEnd).getTime()

    return sortOrder === "asc" ? aLockTime - bLockTime : bLockTime - aLockTime
  })

/**
 * Function that will find the nearest longer lock period from `LOCK_TIMES`.
 *
 * Example:
 * As of writing this comment we have four lock times specified in LOCK_TIMES:
 * 39, 24, 8 (and 0 for zero lock deposits). If we pass 7,5 as lockPeriod to
 * this function, it will return the nearest longer lock period, which in this
 * case is 8.
 *
 * Other examples:
 *
 * lockPeriod passed: 22; return value: 24
 * lockPeriod passed: 8.0943; return value: 24
 * lockPeriod passed: 7.99; return value: 8
 * lockPeriod passed: 40; return value: null
 *
 * @param lockPeriod - lock period in weeks. It can contains decimals, so 7 and
 * a half week would be equal to 7.5.
 * @returns closest period value from `LOCK_TIMES`, that is greater than
 * lockPeriod. Return null if there is no longer period.
 */
export const findNearestLongerPeriod = (lockPeriod: number) => {
  const longerPeriods = LOCK_TIMES.map((lockTime) => lockTime.period).filter(
    (period) => period > lockPeriod,
  )

  if (longerPeriods.length === 0) return null

  // Return the nearest longer period
  return Math.min(...longerPeriods)
}

export const getDepositMigrationStatus = (
  token: SupportedTokenName,
  migration?: DepositMigrationResult,
): DepositMigrationStatus => {
  if (!MIGRATABLE_TOKENS.includes(token))
    return DepositMigrationStatus.NotEligible
  if (migration?.returnTransactionId) return DepositMigrationStatus.Completed
  if (migration?.requestTransactionId) return DepositMigrationStatus.InProgress

  return DepositMigrationStatus.NotInitiated
}
